import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
    fetchBankStatementCashflowExportService,
    CashflowExportPayload,
} from '../../../../../../store/dashboard/financials/entityRationaleServices';
import { useLatestRequestGuard } from '../../../../../../common/hooks/useLatestRequestGuard';
import { downloadBlobFromUrl } from '../../../../../../utils/downloadUtils';
import { JAVA_API_STATUS } from '../../../../../../utils/axios-interceptor';
import { CashflowUnit } from './types';

const GENERIC_EXPORT_ERROR = 'Failed to generate Excel. Please try again.';

type DateRange = { startDate: string; endDate: string } | null;

/** Build the cashflow-export payload, omitting the date window unless both dates are applied
 *  and joining the selected account ids into the CSV the Unifier expects. */
function buildExportPayload(dateRange: DateRange, unit: CashflowUnit, accountIds?: string[]): CashflowExportPayload {
    return {
        ...(dateRange?.startDate && dateRange?.endDate ? { startDate: dateRange.startDate, endDate: dateRange.endDate } : {}),
        unit,
        ...(accountIds?.length ? { accountIds: accountIds.join(',') } : {}),
    };
}

/**
 * Owns the "download cashflow as Excel" flow for a single org (via the BEFE Unifier route).
 *
 * Returns `handleExport` — which guards the org id, derives the service payload, and starts
 * an export for the hook's current org — and `loading`, the latest export's in-flight flag.
 *
 * Concurrency is request-scoped: every export captures a monotonic sequence id and only the
 * latest one may clear the shared loading / in-flight state (`finish`). A superseded export
 * (e.g. one left in flight when the user switches org) can neither download its file nor
 * reset a newer export's state.
 */
export function useCashflowExport(orgId?: string) {
    const [loading, setLoading] = useState(false);
    const cancelledRef = useRef(false);
    const inFlightRef = useRef(false);
    const currentOrgIdRef = useRef<string | undefined>(orgId);
    // Monotonic request token (the repo's shared out-of-order guard): startRequest() stamps
    // each export, isCurrent(token) is true only while it is still the latest export.
    const { startRequest, isCurrent } = useLatestRequestGuard();

    useEffect(() => {
        return () => {
            cancelledRef.current = true;
        };
    }, []);

    // Request-scoped cleanup: a tagged call clears the shared loading / in-flight guard only
    // when its request is still the latest, so a superseded export cannot release a newer
    // one's guard or hide its spinner. The org-switch effect calls it unqualified.
    const finish = useCallback(
        (reqId?: number) => {
            if (reqId !== undefined && !isCurrent(reqId)) return;
            inFlightRef.current = false;
            if (!cancelledRef.current) setLoading(false);
        },
        [isCurrent],
    );

    // FinancialsFromBankingV2 does not remount on org navigation — track the current org so
    // an export triggered for org A is discarded once the user is on org B, and release the
    // in-flight guard so org B isn't blocked by org A's pending export.
    useEffect(() => {
        currentOrgIdRef.current = orgId;
        cancelledRef.current = false; // org switch is not an unmount
        finish();
    }, [orgId, finish]);

    // Stream the presigned file to a download, re-checking staleness after every await so a
    // superseded export never writes a file.
    const downloadPresigned = useCallback(
        async (presignedUrl: string, fileName: string, reqId: number, isStale: () => boolean) => {
            const res = await fetch(presignedUrl);
            // Check staleness before reporting a fetch error: a superseded export (e.g. the
            // user switched org while the fetch was pending) must be discarded silently, the
            // same way the thrown-error path suppresses the toast for stale requests.
            if (isStale()) {
                finish(reqId);
                return;
            }
            if (!res.ok) {
                toast.error(GENERIC_EXPORT_ERROR);
                finish(reqId);
                return;
            }
            const blob = await res.blob();
            if (isStale()) {
                finish(reqId);
                return;
            }
            downloadBlobFromUrl(blob, fileName);
            finish(reqId);
        },
        [finish],
    );

    // Perform one export for an explicitly captured org (the staleness anchor).
    const runExport = useCallback(
        async (triggerOrgId: string, dateRange: DateRange, unit: CashflowUnit, accountIds?: string[]) => {
            if (inFlightRef.current) return;
            cancelledRef.current = false;
            const myReqId = startRequest();
            inFlightRef.current = true;
            setLoading(true);

            const isStale = () => cancelledRef.current || !isCurrent(myReqId) || triggerOrgId !== currentOrgIdRef.current;

            try {
                const response = await fetchBankStatementCashflowExportService(
                    triggerOrgId,
                    buildExportPayload(dateRange, unit, accountIds),
                );
                if (isStale()) {
                    finish(myReqId);
                    return;
                }
                if (response.data?.responseData?.responseCode !== JAVA_API_STATUS.SUCCESS) {
                    toast.error(GENERIC_EXPORT_ERROR);
                    finish(myReqId);
                    return;
                }
                // Unifier envelope: UW's AppResponse is stripped and its data placed under responseBody.
                const { presignedUrl, fileName } = response.data?.responseBody?.data ?? {};
                if (!presignedUrl || !fileName) {
                    toast.error(GENERIC_EXPORT_ERROR);
                    finish(myReqId);
                    return;
                }
                await downloadPresigned(presignedUrl, fileName, myReqId, isStale);
            } catch {
                if (!isStale()) {
                    toast.error(GENERIC_EXPORT_ERROR);
                    finish(myReqId);
                }
            }
        },
        [finish, downloadPresigned, startRequest, isCurrent],
    );

    // Public handler: guard the org id, then export for the hook's current org.
    const handleExport = useCallback(
        (dateRange: DateRange, unit: CashflowUnit, accountIds?: string[]) => {
            if (!orgId) {
                console.error('Organization ID not found');
                return;
            }
            void runExport(orgId, dateRange, unit, accountIds);
        },
        [orgId, runExport],
    );

    return { handleExport, loading };
}
